import { putArtifact } from 'features/knowledgeDocumentViewer/artifacts/artifactStore'
import {
  appendDocChatStreamEvent,
  getDocChatTurnStatus,
  markDocChatTurnCancelled,
  markDocChatTurnError,
  receiveDocChatResponse,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import {
  DOC_CHAT_CHART_ACK_PATH,
  DOC_CHAT_START_PATH,
  postDocChatChartAck,
  postDocChatStop,
  postDocChatStream,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFetcher'
import {
  DocChatRequestSchema,
  type DocChatSseChartCommandEvent,
  type DocChatSseEvent,
  isDocChatKeepaliveSseEvent,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  applyTrendChartCommand,
  trendPreviewScope,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreviewStore'
import type { DocChatConversation } from 'features/knowledgeDocumentViewer/types'
import { pendingAgenticConversationKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import type { GeckoLocale } from 'types/measurement/geckoLocale'

import type { DocChatStopOutcome } from './docChatStopOutcome'

type DocChatStreamListener = (conversation: DocChatConversation) => void

type DocChatStreamPhase =
  | 'queued'
  | 'awaiting_acceptance'
  | 'accepted'
  | 'stop_awaiting_acceptance'
  | 'stopping'
  | 'stopped'

export const UNSUPPORTED_CHART_FIELDS_ERROR =
  'band and aggregation are not shown by the embedded chart'

export type DocChatStreamUpdate = {
  conversation: DocChatConversation
  keys: string[]
  siteId: string
  tabId?: string
}

type DocChatStreamEntry = {
  abortController: AbortController
  conversation: DocChatConversation
  keys: Set<string>
  siteId: string
  turnId: string
  tabId?: string
  authorization?: string
  chartAckUrl?: string
  phase: DocChatStreamPhase
  stopUrl?: string
  finishStop?: () => void
}

type StoppedDocChatTurn = { conversationId?: string; turnId: string }

const entriesByKey = new Map<string, DocChatStreamEntry>()
const pendingStopsByKey = new Map<string, Promise<StoppedDocChatTurn[]>>()
const listenersByKey = new Map<string, Set<DocChatStreamListener>>()
const globalListeners = new Set<(update: DocChatStreamUpdate) => void>()

function lookupKeys(
  conversation: DocChatConversation,
  tabId?: string
): string[] {
  const keys: string[] = []
  if (conversation.id) keys.push(conversation.id)
  if (conversation.pendingSessionKey) keys.push(conversation.pendingSessionKey)
  if (tabId) keys.push(pendingAgenticConversationKey(tabId))
  return [...new Set(keys)]
}

function findEntry(sessionKey: string): DocChatStreamEntry | undefined {
  return entriesByKey.get(sessionKey)
}

function hasStopRequested(entry: DocChatStreamEntry): boolean {
  return (
    entry.phase === 'stop_awaiting_acceptance' ||
    entry.phase === 'stopping' ||
    entry.phase === 'stopped'
  )
}

function findEntryForConversation(
  conversation: DocChatConversation,
  tabId?: string
): DocChatStreamEntry | undefined {
  for (const key of lookupKeys(conversation, tabId)) {
    const entry = findEntry(key)
    if (entry) return entry
  }
  const loadingTurnIds = new Set(
    conversation.turns
      .filter((turn) => getDocChatTurnStatus(turn) === 'loading')
      .map((turn) => turn.id)
  )
  if (conversation.activeTurnId) loadingTurnIds.add(conversation.activeTurnId)
  if (loadingTurnIds.size === 0) return undefined
  const seen = new Set<DocChatStreamEntry>()
  for (const entry of entriesByKey.values()) {
    if (seen.has(entry)) continue
    seen.add(entry)
    if (loadingTurnIds.has(entry.turnId)) return entry
  }
  return undefined
}
function indexEntry(entry: DocChatStreamEntry): void {
  entry.keys.forEach((key) => {
    entriesByKey.set(key, entry)
  })
}

function notify(entry: DocChatStreamEntry): void {
  const listeners = new Set(
    [...entry.keys].flatMap((key) => [...(listenersByKey.get(key) ?? [])])
  )
  listeners.forEach((listener) => listener(entry.conversation))
  const update: DocChatStreamUpdate = {
    conversation: entry.conversation,
    keys: [...entry.keys],
    siteId: entry.siteId,
    tabId: entry.tabId,
  }
  globalListeners.forEach((listener) => listener(update))
}

function finish(entry: DocChatStreamEntry): void {
  entry.keys.forEach((key) => {
    if (entriesByKey.get(key) === entry) entriesByKey.delete(key)
  })
}

function commit(
  entry: DocChatStreamEntry,
  conversation: DocChatConversation
): void {
  entry.conversation = conversation
  if (conversation.id) entry.keys.add(conversation.id)
  if (conversation.pendingSessionKey) {
    entry.keys.add(conversation.pendingSessionKey)
  }
  indexEntry(entry)
  notify(entry)
}

export function registerDocChatStream({
  abortController = new AbortController(),
  conversation,
  siteId,
  tabId,
}: {
  abortController?: AbortController
  conversation: DocChatConversation
  siteId: string
  tabId?: string
}): string | undefined {
  const turnId = conversation.activeTurnId
  if (!turnId) return undefined
  const keys = lookupKeys(conversation, tabId)
  const existingKey = keys.find((key) => {
    const entry = findEntry(key)
    return entry && !hasStopRequested(entry)
  })
  if (existingKey) return existingKey
  if (keys.length === 0) return undefined
  const entry: DocChatStreamEntry = {
    abortController,
    conversation,
    keys: new Set(keys),
    siteId,
    turnId,
    tabId,
    phase: 'queued',
  }
  // Follow-ups replace stopped entries here; pendingStopsByKey retains their
  // cancellation so the next POST still waits for the server's running turn.
  indexEntry(entry)
  return keys[0]
}

export function applyDocChatStreamEvent({
  sessionKey,
  streamEvent,
}: {
  sessionKey: string
  streamEvent: DocChatSseEvent
}): DocChatConversation | undefined {
  const entry = findEntry(sessionKey)
  if (!entry) return undefined
  return applyEntryStreamEvent(entry, streamEvent)
}

function isCurrentEntry(entry: DocChatStreamEntry): boolean {
  return [...entry.keys].some((key) => findEntry(key) === entry)
}

function applyEntryStreamEvent(
  entry: DocChatStreamEntry,
  streamEvent: DocChatSseEvent
): DocChatConversation | undefined {
  if (hasStopRequested(entry)) {
    if (
      entry.phase === 'stop_awaiting_acceptance' &&
      streamEvent.type === 'started'
    ) {
      entry.phase = 'stopping'
      const conversation = appendDocChatStreamEvent({
        conversation: entry.conversation,
        streamEvent,
        siteId: entry.siteId,
      })
      if (isCurrentEntry(entry)) commit(entry, conversation)
      else entry.conversation = conversation
      cancelAcceptedEntry(entry)
    }
    return undefined
  }
  if (!isCurrentEntry(entry)) return undefined
  if (streamEvent.type === 'started') entry.phase = 'accepted'
  if (streamEvent.type === 'chart_command') {
    applyChartCommandEvent(entry, streamEvent)
    return entry.conversation
  }
  if (streamEvent.type === 'artifact') putArtifact(streamEvent.artifact)
  const nextConversation = appendDocChatStreamEvent({
    conversation: entry.conversation,
    streamEvent,
    siteId: entry.siteId,
  })
  const erroredConversation =
    streamEvent.type === 'error'
      ? markDocChatTurnError({
          conversation: nextConversation,
          turnId: entry.turnId,
          siteId: entry.siteId,
        })
      : nextConversation
  commit(entry, erroredConversation)
  if (isDocChatKeepaliveSseEvent(streamEvent)) {
    return erroredConversation
  }
  finish(entry)
  return erroredConversation
}

export function getDocChatStreamConversation(
  sessionKey: string
): DocChatConversation | undefined {
  return findEntry(sessionKey)?.conversation
}

/**
 * The agent asked the chart beside this chat to change. Apply it to the trend
 * preview and tell the turn what the chart now shows; the agent only claims
 * the chart changed once that answer arrives.
 */
function applyChartCommandEvent(
  entry: DocChatStreamEntry,
  streamEvent: DocChatSseChartCommandEvent
): void {
  const turn = entry.conversation.turns.find((t) => t.id === entry.turnId)
  const state = applyTrendChartCommand(
    trendPreviewScope(entry.tabId),
    streamEvent.command,
    streamEvent.command.label ?? turn?.query ?? streamEvent.query
  )
  if (!entry.chartAckUrl || !entry.authorization) return
  const unsupported = Boolean(
    streamEvent.command.band || streamEvent.command.aggregation_op
  )
  void postDocChatChartAck({
    url: entry.chartAckUrl,
    authorization: entry.authorization,
    body: {
      conversation_id: streamEvent.conversation_id,
      command_id: streamEvent.command.command_id,
      applied: true,
      state,
      ...(unsupported ? { error: UNSUPPORTED_CHART_FIELDS_ERROR } : {}),
    },
  }).catch(() => undefined)
}

export function isDocChatTurnInFlight(
  conversation: DocChatConversation,
  turnId: string,
  tabId?: string
): boolean {
  return lookupKeys(conversation, tabId).some((key) => {
    const entry = findEntry(key)
    return (
      entry !== undefined && entry.turnId === turnId && !hasStopRequested(entry)
    )
  })
}

/**
 * Subscribe may run before POST. Queue until register. Unsubscribe does not abort.
 */
export function subscribeDocChatStream(
  sessionKey: string,
  listener: DocChatStreamListener
): () => void {
  const listeners = listenersByKey.get(sessionKey) ?? new Set()
  listeners.add(listener)
  listenersByKey.set(sessionKey, listeners)
  return () => {
    const currentListeners = listenersByKey.get(sessionKey)
    currentListeners?.delete(listener)
    if (currentListeners?.size === 0) listenersByKey.delete(sessionKey)
  }
}

export function subscribeDocChatStreamUpdates(
  listener: (update: DocChatStreamUpdate) => void
): () => void {
  globalListeners.add(listener)
  return () => {
    globalListeners.delete(listener)
  }
}

export function startDocChatStream({
  authorization,
  conversation,
  locale,
  requestBody,
  siteId,
  tabId,
  url,
}: {
  authorization: string
  conversation: DocChatConversation
  locale: GeckoLocale
  requestBody: string
  siteId: string
  tabId?: string
  url: string
}): void {
  const turnId = conversation.activeTurnId
  if (!turnId) return
  if (isDocChatTurnInFlight(conversation, turnId, tabId)) return
  const pendingStops = [
    ...new Set(
      lookupKeys(conversation, tabId).flatMap((key) => {
        const pending = pendingStopsByKey.get(key)
        return pending ? [pending] : []
      })
    ),
  ]
  const sessionKey = registerDocChatStream({
    conversation,
    siteId,
    tabId,
  })
  if (!sessionKey) return
  const entry = findEntry(sessionKey)
  if (!entry) return
  entry.tabId = tabId
  entry.authorization = authorization
  entry.chartAckUrl = url.endsWith(DOC_CHAT_START_PATH)
    ? url.slice(0, -DOC_CHAT_START_PATH.length) + DOC_CHAT_CHART_ACK_PATH
    : undefined
  commit(entry, {
    ...conversation,
    turns: conversation.turns.map((turn) =>
      turn.id === turnId ? { ...turn, requestStarted: true } : turn
    ),
  })
  const send = (stoppedTurns: StoppedDocChatTurn[] = []) => {
    if (hasStopRequested(entry)) return
    const body = inheritConversationIdFromStops(
      entry,
      stoppedTurns,
      requestBody
    )
    entry.phase = 'awaiting_acceptance'
    return postDocChatStream({
      url,
      body,
      authorization,
      locale,
      signal: entry.abortController.signal,
      onEvent: (streamEvent) => applyEntryStreamEvent(entry, streamEvent),
      onResponseReceived: (response) => {
        if (hasStopRequested(entry) || !isCurrentEntry(entry)) return
        commit(
          entry,
          receiveDocChatResponse({
            conversation: entry.conversation,
            response,
            siteId: entry.siteId,
          })
        )
      },
    })
  }
  // The next prompt can appear immediately, but its POST must wait for the
  // previous worker to release the conversation's running-turn claim.
  const stream = pendingStops.length
    ? Promise.all(pendingStops).then((stops) => send(stops.flat()))
    : send()
  void stream?.catch(() => {
    if (hasStopRequested(entry)) {
      if (entry.phase === 'stop_awaiting_acceptance') entry.finishStop?.()
      return
    }
    if (entry.abortController.signal.aborted || !isCurrentEntry(entry)) return
    const current = entry
    const activeTurn = current.conversation.turns.find(
      (turn) => turn.id === current.turnId
    )
    if (!activeTurn || getDocChatTurnStatus(activeTurn) !== 'loading') return
    commit(
      current,
      markDocChatTurnError({
        conversation: current.conversation,
        turnId: current.turnId,
        siteId: current.siteId,
      })
    )
    finish(current)
  })
}

function inheritConversationIdFromStops(
  entry: DocChatStreamEntry,
  stoppedTurns: StoppedDocChatTurn[],
  requestBody: string
): string {
  if (entry.conversation.id) return requestBody
  const stoppedTurn = stoppedTurns.find(
    (stopped) =>
      stopped.conversationId &&
      entry.conversation.turns.some((turn) => turn.id === stopped.turnId)
  )
  if (!stoppedTurn?.conversationId) return requestBody
  const body = JSON.stringify({
    ...DocChatRequestSchema.parse(JSON.parse(requestBody)),
    conversation_id: stoppedTurn.conversationId,
  })
  commit(entry, { ...entry.conversation, id: stoppedTurn.conversationId })
  return body
}

function trackPendingStop(
  keys: string[],
  pending: Promise<StoppedDocChatTurn>
): void {
  const previous = [
    ...new Set(
      keys.flatMap((key) => {
        const stop = pendingStopsByKey.get(key)
        return stop ? [stop] : []
      })
    ),
  ]
  const settled = Promise.all([Promise.all(previous), pending]).then(
    ([previousStops, stopped]) => [...previousStops.flat(), stopped]
  )
  keys.forEach((key) => pendingStopsByKey.set(key, settled))
  void settled.finally(() => {
    keys.forEach((key) => {
      if (pendingStopsByKey.get(key) === settled) pendingStopsByKey.delete(key)
    })
  })
}

function cancelAcceptedEntry(entry: DocChatStreamEntry): void {
  const conversationId = entry.conversation.id
  if (!conversationId || !entry.stopUrl || !entry.authorization) return
  void postDocChatStop({
    url: entry.stopUrl,
    authorization: entry.authorization,
    conversationId,
    siteId: entry.siteId,
    turnId: entry.turnId,
  })
    .catch(() => undefined)
    .finally(() => entry.finishStop?.())
  entry.abortController.abort()
}

export async function stopDocChatStream({
  conversation,
  tabId,
  authorization,
  siteId,
  url,
}: {
  conversation: DocChatConversation
  tabId?: string
  authorization: string
  siteId: string
  url: string
}): Promise<DocChatStopOutcome | undefined> {
  const entry = findEntryForConversation(conversation, tabId)
  const turnId = entry?.turnId ?? conversation.activeTurnId
  if (!turnId) return undefined
  if (entry) {
    if (hasStopRequested(entry)) return { turnId, status: 'cancelled' }
    const phase = entry.phase
    entry.phase =
      phase === 'awaiting_acceptance' ? 'stop_awaiting_acceptance' : 'stopping'
    entry.authorization = authorization
    entry.stopUrl = url
    const pending = new Promise<StoppedDocChatTurn>((resolve) => {
      entry.finishStop = () => {
        entry.phase = 'stopped'
        finish(entry)
        resolve({ conversationId: entry.conversation.id, turnId })
      }
    })
    trackPendingStop([...entry.keys], pending)
    commit(
      entry,
      markDocChatTurnCancelled({
        conversation: entry.conversation,
        turnId,
      })
    )
    switch (phase) {
      case 'queued':
        entry.abortController.abort()
        entry.finishStop?.()
        break
      case 'awaiting_acceptance':
        // Keep the stream open only to learn which server turn to cancel.
        break
      case 'accepted':
        cancelAcceptedEntry(entry)
        break
    }
    return { turnId, status: 'cancelled' }
  }
  const activeTurn = conversation.turns.find((turn) => turn.id === turnId)
  if (activeTurn?.restoredRunning && conversation.id) {
    const pending = postDocChatStop({
      url,
      authorization,
      conversationId: conversation.id,
      siteId,
      turnId,
    }).then(
      () => ({ conversationId: conversation.id, turnId }),
      () => ({ conversationId: conversation.id, turnId })
    )
    trackPendingStop(lookupKeys(conversation, tabId), pending)
  }
  return { turnId, status: 'cancelled' }
}

export function resetDocChatInFlightStore(): void {
  entriesByKey.clear()
  pendingStopsByKey.clear()
  listenersByKey.clear()
  globalListeners.clear()
}
